import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle, Mail, Sun, Moon } from '@/components/icons';
import { authClient } from '../../lib/auth-client';
import { useTheme } from '../../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import StackSketchLogo from '/StackSketch-FullLogo.svg';

type VerificationStatus = 'loading' | 'success' | 'error' | 'no-token';

export function VerifyEmailPage() {
  const { isDark, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        const result = await authClient.verifyEmail({
          query: { token },
        });

        if (result.error) {
          setStatus('error');
          setErrorMessage(result.error.message || 'Failed to verify email');
        } else {
          setStatus('success');
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage('An unexpected error occurred while verifying your email');
        console.error(err);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-background [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]">
      {/* Logo and branding */}
      <div className="mb-8 text-center">
        <img src={StackSketchLogo} alt="Stack Sketch" className="w-32 h-auto mx-auto mb-4 dark:brightness-0 dark:invert" />
        <p className="text-muted-foreground dark:text-muted-foreground">
          Email Verification
        </p>
      </div>

      {/* Verification Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="border-border dark:border-border shadow-xl">
          <CardHeader className="border-b border-border dark:border-border text-center">
            <h3 className="text-lg font-bold text-foreground dark:text-foreground">
              {status === 'loading' && 'Verifying your email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
              {status === 'no-token' && 'Invalid Link'}
            </h3>
            <CardDescription className="text-xs">
              {status === 'loading' && 'Please wait while we verify your email address'}
              {status === 'success' && 'Your email has been successfully verified'}
              {status === 'error' && 'We could not verify your email address'}
              {status === 'no-token' && 'This verification link appears to be invalid'}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center py-8">
              {status === 'loading' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-4"
                >
                  <Loader2 className="w-16 h-16 text-primary animate-spin" />
                  <p className="text-muted-foreground dark:text-muted-foreground text-center">
                    Verifying your email address...
                  </p>
                </motion.div>
              )}

              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center gap-4"
                >
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-foreground dark:text-foreground font-medium mb-2">
                      Your email has been verified!
                    </p>
                    <p className="text-muted-foreground dark:text-muted-foreground text-sm">
                      Redirecting you to the dashboard...
                    </p>
                  </div>
                  <Link to="/" className="mt-4">
                    <Button>Go to Dashboard</Button>
                  </Link>
                </motion.div>
              )}

              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center gap-4 w-full"
                >
                  <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                    <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
                  </div>
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                  <p className="text-muted-foreground dark:text-muted-foreground text-sm text-center mt-2">
                    The verification link may have expired or already been used.
                  </p>
                  <div className="flex flex-col gap-2 w-full mt-4">
                    <Link to="/auth" className="w-full">
                      <Button className="w-full" variant="default">
                        Back to Sign In
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              )}

              {status === 'no-token' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center gap-4"
                >
                  <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                    <Mail className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-foreground dark:text-foreground font-medium mb-2">
                      Invalid verification link
                    </p>
                    <p className="text-muted-foreground dark:text-muted-foreground text-sm">
                      Please check your email for the correct verification link.
                    </p>
                  </div>
                  <Link to="/auth" className="mt-4">
                    <Button>Back to Sign In</Button>
                  </Link>
                </motion.div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="fixed bottom-4 right-4 p-3 bg-card dark:bg-card border border-border dark:border-border rounded-full shadow-lg hover:bg-muted dark:hover:bg-muted transition-colors"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>
    </div>
  );
}
